import { useCallback, useEffect, useState } from 'react';
import { FormikHelpers } from 'formik';

import { IComposePanelFields } from './compose-panel.types';
import { COMPOSE_FORM_ID } from './compose-panel.constants';
import { buildInitialValues } from './compose-panel.helpers';
import { buildFieldTokenMessages } from './compose-panel.validation';
import { buildAdHocInput, describeSendRefusal } from './compose-panel.send';
import { focusFirstInvalidField } from '../../_core/Formik/focus-first-invalid-field.helpers';
import { setCommunicationSendInFlight } from '../../../lib/stores/communication.store';
import {
  IAdHocCommunicationInput,
  IAdHocCommunicationResult,
  IAdHocTokenReport
} from '../../../lib/types/communication.types';

// The mutation-result wrapper the send flow reads; `data` is null on a GraphQL
// or network error. Real sends key on `queued`; test sends key on `sent`.
interface ISendAdHocResult {
  data?: { sendAdHocCommunication?: IAdHocCommunicationResult | null } | null;
}

interface IUseComposeSendArgs {
  panelAssessmentId?: string;
  sessionTerm: string;
  sendAdHocCommunication: (input: IAdHocCommunicationInput) => Promise<ISendAdHocResult | null | undefined>;
  waitForFreshReport: () => Promise<IAdHocTokenReport | undefined>;
  retireDraftAfterSend: () => void;
  resetContent: () => void;
}

interface IUseComposeSendReturn {
  handleSubmit: (values: IComposePanelFields, helpers: FormikHelpers<IComposePanelFields>) => Promise<void>;
  handleSendTest: (
    values: IComposePanelFields,
    testEmails: string[],
    testPhones: string[],
    testSourceId?: string
  ) => Promise<number>;
  handleComposeAnother: () => void;
  submitError?: string;
  // Count of messages accepted into the delivery queue on a successful real send;
  // drives the "Queued N messages" success view. Undefined until a send succeeds.
  queuedCount?: number;
}

// HOOK: owns the compose panel's send/test-send system — submit + test handlers, the
// fresh-token-report gate, the sent-count outcome branches, and success/error state.
export const useComposeSend = ({
  panelAssessmentId,
  sessionTerm,
  sendAdHocCommunication,
  waitForFreshReport,
  retireDraftAfterSend,
  resetContent
}: IUseComposeSendArgs): IUseComposeSendReturn => {
  const [submitError, setSubmitError] = useState<string | undefined>();
  const [queuedCount, setQueuedCount] = useState<number | undefined>();

  // When the panel scopes to a different assessment (route navigation), wipe
  // transient send state so a stale success/error doesn't attach to the new one.
  useEffect(() => {
    setQueuedCount(undefined);
    setSubmitError(undefined);
  }, [panelAssessmentId]);

  const handleSubmit = useCallback(async (
    values: IComposePanelFields,
    { setSubmitting, resetForm }: FormikHelpers<IComposePanelFields>
  ) => {
    setSubmitError(undefined);

    if (!panelAssessmentId) {
      setSubmitError('No assessment available to send from');
      setSubmitting(false);
      return;
    }

    // In-flight from before the report await so tab switching stays locked for
    // the whole submit — the container can't unmount under a parked waiter.
    setCommunicationSendInFlight(true);
    try {
      // Token validation gates the send on a FRESH server report (the debounced
      // call may be in flight or trailing an edit); the server backstop remains.
      const report = await waitForFreshReport();
      const tokenErrorFields = Object.keys(buildFieldTokenMessages(report, values, sessionTerm));
      if (tokenErrorFields.length > 0) {
        // Content fields are disabled while submitting; defer the focus past the
        // finally's setSubmitting(false) re-render so it lands on a re-enabled field.
        requestAnimationFrame(() => {
          const formEl = document.getElementById(COMPOSE_FORM_ID);
          if (formEl) focusFirstInvalidField(formEl, tokenErrorFields);
        });
        return;
      }

      const result = await sendAdHocCommunication(buildAdHocInput({ values, panelAssessmentId }));

      const data = result && result.data && result.data.sendAdHocCommunication;
      // Real sends enqueue: `queued` is the success signal; a missing count is a
      // hard failure, not a zero — see ComposePanel/README.md "Send-response handling".
      if (!data || typeof data.queued !== 'number') {
        setSubmitError('Send failed');
        return;
      }

      // Zero queued is the honest zero-recipient failure — see ComposePanel/README.md
      // "Send-response handling".
      if (data.queued === 0) {
        setSubmitError(
          'No messages were queued — every recipient was already covered, duplicated, or unreachable.'
        );
        return;
      }

      // Enqueue success IS the send's success — delivery happens in the
      // background; the History tab is where the user tracks it settling.
      retireDraftAfterSend();
      setQueuedCount(data.queued);
      resetForm({ values: buildInitialValues() });
    } catch (err) {
      console.error('sendAdHocCommunication failed:', err);
      // `||` (not `??`) so an empty-string refusal falls through to the
      // user-facing fallback — see REACT_CONVENTIONS.md user-facing copy fallbacks.
      setSubmitError(describeSendRefusal(err, values.contexts) || 'Send failed');
    } finally {
      setSubmitting(false);
      // Released at the mutation's settled point; a leaked `true` is bounded by
      // the defensive reset in openCommunicationPanel.
      setCommunicationSendInFlight(false);
    }
  }, [panelAssessmentId, sendAdHocCommunication, retireDraftAfterSend, waitForFreshReport, sessionTerm]);

  // Test send — delivers to typed-in addresses but carries the real target context
  // so context-scoped variables still render; reuses the send mutation with test:true.
  const handleSendTest = useCallback(async (
    values: IComposePanelFields,
    testEmails: string[],
    testPhones: string[],
    testSourceId?: string
  ): Promise<number> => {
    if (!panelAssessmentId) throw new Error('No assessment available to send from');

    const result = await sendAdHocCommunication(
      buildAdHocInput({ values, panelAssessmentId, test: true, testEmails, testPhones, testSourceId })
    );

    const data = result && result.data && result.data.sendAdHocCommunication;
    if (!data || typeof data.sent !== 'number') throw new Error('Test send failed');
    return data.sent;
  }, [panelAssessmentId, sendAdHocCommunication]);

  const handleComposeAnother = useCallback(() => {
    setQueuedCount(undefined);
    setSubmitError(undefined);
    resetContent();
  }, [resetContent]);

  return { handleSubmit, handleSendTest, handleComposeAnother, submitError, queuedCount };
};
